import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Filter, X } from 'lucide-react';

const OpportunityFilters = ({ onFilterChange }) => {
  const [showFilters, setShowFilters] = useState(false);
  const [filterOptions, setFilterOptions] = useState({ towns: [], categories: [] });
  const [filters, setFilters] = useState({
    day_of_week: [],
    start_date: '',
    end_date: '',
    town: '',
    categories: [],
    duration_bucket: ''
  });

  const fetchFilterOptions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/opportunities/meta/filter-options`);
      setFilterOptions(response.data);
    } catch (err) {
      console.error('fetchFilterOptions failed', err);
    }
  }, []);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  // Store callback in ref to avoid including it in effect deps
  const onFilterChangeRef = React.useRef(onFilterChange);
  onFilterChangeRef.current = onFilterChange;

  const isInitialMount = React.useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    onFilterChangeRef.current(filters);
  }, [filters]);

  const handleDayToggle = (day) => {
    setFilters(prev => ({
      ...prev,
      day_of_week: prev.day_of_week.includes(day)
        ? prev.day_of_week.filter(d => d !== day)
        : [...prev.day_of_week, day]
    }));
  };

  const handleCategoryToggle = (category) => {
    setFilters(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category]
    }));
  };

  const clearFilters = () => {
    setFilters({
      day_of_week: [],
      start_date: '',
      end_date: '',
      town: '',
      categories: [],
      duration_bucket: ''
    });
  };

  const hasActiveFilters = () => {
    return filters.day_of_week.length > 0 ||
           filters.start_date ||
           filters.end_date ||
           filters.town ||
           filters.categories.length > 0 ||
           filters.duration_bucket;
  };

  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const durations = [
    { label: '0-1 hour', value: '0-60' },
    { label: '1-2 hours', value: '60-120' },
    { label: '2-3 hours', value: '120-180' },
    { label: '3-4 hours', value: '180-240' },
    { label: '4+ hours', value: '240+' },
    { label: 'Flexible', value: 'flexible' }
  ];

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-[#E9DDCF] rounded-lg hover:border-[#8B2E3C] transition-colors"
          data-testid="toggle-filters-button"
        >
          <Filter className="w-4 h-4" />
          <span className="font-medium">Filters</span>
          {hasActiveFilters() && (
            <span className="bg-[#8B2E3C] text-white text-xs px-2 py-0.5 rounded-full">
              Active
            </span>
          )}
        </button>

        {hasActiveFilters() && (
          <button
            onClick={clearFilters}
            className="flex items-center gap-2 text-[#8B2E3C] hover:underline text-sm font-medium"
            data-testid="clear-filters-button"
          >
            <X className="w-4 h-4" />
            Clear all filters
          </button>
        )}
      </div>

      {showFilters && (
        <div className="card space-y-6" data-testid="filters-panel">
          {/* Day of Week */}
          <div>
            <label className="block text-sm font-semibold mb-2">Day of Week</label>
            <div className="flex flex-wrap gap-2">
              {days.map((day, index) => (
                <button
                  key={day}
                  onClick={() => handleDayToggle(index)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    filters.day_of_week.includes(index)
                      ? 'bg-[#8B2E3C] text-white'
                      : 'bg-[#E9DDCF] text-[#222222] hover:bg-[#C9A227]'
                  }`}
                  data-testid={`day-filter-${index}`}
                >
                  {day}
                </button>
              ))}
            </div>
          </div>

          {/* Date Range */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold mb-2">Start Date</label>
              <input
                type="date"
                className="input-field"
                value={filters.start_date}
                onChange={(e) => setFilters(prev => ({ ...prev, start_date: e.target.value }))}
                data-testid="start-date-filter"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2">End Date</label>
              <input
                type="date"
                className="input-field"
                value={filters.end_date}
                onChange={(e) => setFilters(prev => ({ ...prev, end_date: e.target.value }))}
                data-testid="end-date-filter"
              />
            </div>
          </div>

          {/* Town */}
          <div>
            <label className="block text-sm font-semibold mb-2">Town</label>
            <select
              className="input-field"
              value={filters.town}
              onChange={(e) => setFilters(prev => ({ ...prev, town: e.target.value }))}
              data-testid="town-filter"
            >
              <option value="">All Towns</option>
              {filterOptions.towns.map(town => (
                <option key={town} value={town}>{town}</option>
              ))}
            </select>
          </div>

          {/* Categories */}
          {filterOptions.categories.length > 0 && (
            <div>
              <label className="block text-sm font-semibold mb-2">Type of Work</label>
              <div className="flex flex-wrap gap-2">
                {filterOptions.categories.map(category => (
                  <button
                    key={category}
                    onClick={() => handleCategoryToggle(category)}
                    className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                      filters.categories.includes(category)
                        ? 'bg-[#8B2E3C] text-white'
                        : 'bg-[#E9DDCF] text-[#222222] hover:bg-[#C9A227]'
                    }`}
                    data-testid={`category-filter-${category}`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Duration */}
          <div>
            <label className="block text-sm font-semibold mb-2">Time Commitment</label>
            <select
              className="input-field"
              value={filters.duration_bucket}
              onChange={(e) => setFilters(prev => ({ ...prev, duration_bucket: e.target.value }))}
              data-testid="duration-filter"
            >
              <option value="">Any Duration</option>
              {durations.map(duration => (
                <option key={duration.value} value={duration.value}>{duration.label}</option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

export default OpportunityFilters;
